import { useEffect, useState } from "react";
import { Check, Image as ImageIcon, MessageSquarePlus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, inr, StatusBadge } from "@/lib/api";
import { ActionDialog, Btn, PageHead } from "@/admin/ui";

const TABS = [["under_verification", "To verify"], ["awaiting_payment,rejected", "Awaiting"], ["paid", "Paid"], ["", "All"]];

const ShotViewer = ({ path, onClose }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u;
    api.get(`/admin/files/${path}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch((e) => toast.error(errMsg(e)));
    return () => u && URL.revokeObjectURL(u);
  }, [path]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-[22px] bg-pearl" data-testid="screenshot-viewer">
        <DialogTitle className="font-display text-[26px]">Payment screenshot</DialogTitle>
        {url ? <img src={url} alt="Payment screenshot" className="w-full rounded-[16px]" /> : <div className="h-80 rounded-[16px] bg-white animate-pulse" />}
        <p className="text-[13px] text-plum/60">A screenshot is not proof of payment. Always confirm the amount and UTR in your bank / UPI merchant account.</p>
      </DialogContent>
    </Dialog>
  );
};

export default function Payments() {
  const [tab, setTab] = useState("under_verification");
  const [list, setList] = useState(null);
  const [act, setAct] = useState(null);
  const [shot, setShot] = useState(null);
  const load = () => api.get("/admin/orders", { params: tab ? { payment_status: tab } : {} }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [tab]); // eslint-disable-line
  const run = async (kind, o, note) => {
    await api.post(`/admin/payments/${o.order_number}/${kind}`, { note: note || null });
    toast.success(kind === "verify" ? "Payment verified — order marked Paid" : kind === "reject" ? "Payment rejected — customer can resubmit" : "Note added");
    load();
  };

  return (
    <div data-testid="admin-payments">
      <PageHead title="Payments" sub="Check each UTR against your UPI / bank statement before verifying." />
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-5">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`h-10 px-4 rounded-[12px] text-[13px] font-bold whitespace-nowrap ${tab === k ? "bg-plum text-pearl" : "bg-white border border-plum/10"}`} data-testid={`payments-tab-${k.split(",")[0] || "all"}`}>{l}</button>)}
      </div>
      <div className="space-y-3">
        {list?.length === 0 && <p className="rounded-[22px] bg-white border border-plum/10 p-6 text-plum/60">Nothing here right now.</p>}
        {(list || []).map((o) => (
          <div key={o.order_number} className="rounded-[22px] bg-white border border-plum/10 p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid={`payment-row-${o.order_number}`}>
            <div className="w-24"><p className="font-extrabold">{o.order_number}</p><p className="text-[12px] text-plum/55">{new Date(o.created_at).toLocaleDateString("en-IN")}</p></div>
            <div className="flex-1 min-w-[140px]"><p className="font-bold">{o.customer.name}</p><p className="text-[13px] text-plum/60">{o.customer.mobile}</p></div>
            <p className="font-display text-[26px] font-semibold w-28" data-testid={`payment-amount-${o.order_number}`}>{inr(o.total)}</p>
            <div className="w-40"><p className="text-[12px] font-bold text-plum/55 uppercase tracking-wider">UTR</p><p className="font-extrabold tracking-wider" data-testid={`payment-utr-${o.order_number}`}>{o.utr || o.rejected_utr || "—"}</p></div>
            {o.has_screenshot ? <Btn variant="light" onClick={() => setShot(o.screenshot_path)} data-testid={`view-screenshot-${o.order_number}`}><ImageIcon className="w-4 h-4" />Screenshot</Btn> : <span className="text-[13px] text-plum/45 w-28">No screenshot</span>}
            <StatusBadge status={o.payment_status} testId={`payment-status-${o.order_number}`} />
            <div className="flex gap-2 w-full sm:w-auto">
              {["under_verification", "awaiting_payment"].includes(o.payment_status) && o.status !== "cancelled" && <>
                <Btn variant="green" onClick={() => setAct({ kind: "verify", o })} data-testid={`verify-payment-${o.order_number}`}><Check className="w-4 h-4" />Verify</Btn>
                <Btn variant="red" onClick={() => setAct({ kind: "reject", o })} data-testid={`reject-payment-${o.order_number}`}><X className="w-4 h-4" />Reject</Btn>
              </>}
              <Btn variant="light" onClick={() => setAct({ kind: "note", o })} aria-label="Add note" data-testid={`note-payment-${o.order_number}`}><MessageSquarePlus className="w-4 h-4" /></Btn>
            </div>
          </div>
        ))}
      </div>
      {act && (
        <ActionDialog open onClose={() => setAct(null)} testId={`${act.kind}-dialog`}
          title={act.kind === "verify" ? "Verify this payment?" : act.kind === "reject" ? "Reject this payment?" : "Add an internal note"}
          description={act.kind === "verify" ? `Only continue if you can see ${inr(act.o.total)} received in your account${act.o.utr ? ` with UTR ${act.o.utr}` : ""}.` : act.kind === "reject" ? "The customer will see your reason and can submit a correct UTR." : `Order ${act.o.order_number}`}
          confirmLabel={act.kind === "verify" ? "Yes, mark as Paid" : act.kind === "reject" ? "Reject payment" : "Save note"}
          variant={act.kind === "verify" ? "green" : act.kind === "reject" ? "red" : "dark"}
          withNote notePlaceholder={act.kind === "reject" ? "Reason shown to customer (optional)" : "Internal note (optional)"}
          onConfirm={(note) => run(act.kind, act.o, note)} />
      )}
      {shot && <ShotViewer path={shot} onClose={() => setShot(null)} />}
    </div>
  );
}
